import * as AvailabilityConstants from "./availability-constants";
import * as AvailabilityModels from "./availability-models";
import { LoggingService } from "../loggingSvc";
import * as ChannelsModel from "../agent-channels/channels-models";
import { ChannelsConstants } from "../agent-channels/channels-constants";
import * as ChannelService from "../agent-channels/channels-service";
import { OutgoingQosEvent } from "../../../ShellCommon/logger/telemetry-service";

export class AvailabilityServiceImpl implements AvailabilityModels.AvailabilityService {
  private agentSdk: ChannelsModel.ChannelsSdk = undefined;
  private callbacks: Set<AvailabilityModels.OnAvailabilityEventCallback> =
    new Set<AvailabilityModels.OnAvailabilityEventCallback>();
  private presenceMap: Map<string, ChannelsModel.ModalityAvailability> = new Map<
    string,
    ChannelsModel.ModalityAvailability
  >();
  private isInitialized = false;
  private supportedModalitiesSet: Set<string> = new Set<string>();
  private initializationPromise: ng.IPromise<ChannelsModel.PromiseResult>;

  // @ngInject
  constructor(
    private sdkChannelsService: ChannelService.ChannelsServiceApi,
    private loggingService: LoggingService,
    private $q: ng.IQService,
    private $window: ng.IWindowService,
    private channelsConstants: ChannelsConstants,
    private modalityConstants: AvailabilityConstants.ModalityConstants
  ) {}

  // -- PUBLIC Methods --

  /**
   * Initialize availability service
   * @param supportedModalities List of modalities to support
   * @param callback Callback method to receive notifications
   */
  public init(
    supportedModalities: AvailabilityConstants.Modality[],
    callback: AvailabilityModels.OnAvailabilityEventCallback
  ): ng.IPromise<ChannelsModel.PromiseResult> {
    if (callback != undefined && !this.callbacks.has(callback)) {
      this.callbacks.add(callback);
    }

    supportedModalities.forEach((m) => {
      this.supportedModalitiesSet.add(m.getValue());
    });

    this.initializeDefaultPresence();
    return this.initialize();
  }

  /**
   * Update the presence state for a given modality
   * @param state: the availability state object
   */
  public updatePresence(state: AvailabilityModels.ModalityState): ng.IPromise<ChannelsModel.PromiseResult> {
    return this.initialize()
      .then(() => {
        let params = {
          modalityDetails: [
            {
              modality: state.modality.getValue(),
              availabilityStatus: state.cantileverState.availabilityStatus.getValue(),
              reason: state.cantileverState.reason
            }
          ]
        };
        let outgoingOperationConfig: OutgoingQosEvent = {
          dependencyOperationName: "setAvailability",
          dependencyName: "agentSdk",
          isSuccess: false,
          latencyMs: 0,
          operationName: "updatePresence",
          requestMethod: "post",
          responseStatusCode: "0",
          requestProtocol: "0",
          targetUri: "agentSdk.setAvailability"
        };

        let oldState: ChannelsModel.ModalityAvailability = this.presenceMap.get(state.modality.getValue());
        let startTs = window.performance.now();
        return this.agentSdk
          .setAvailability(params)
          .then(() => {
            this.updatePresenceMap(state);
            let successInfo: any = {
              message: "Agent availability state updated successfully",
              oldState: oldState,
              newState: params.modalityDetails[0],
              windowId: this.$window ? this.$window.name : undefined
            };

            // sdkchannelsService.log isn't reliable, we want to log this in SD
            // will keep the other one as well for now.
            this.loggingService.logInfo(successInfo);
            this.sdkChannelsService.logOutgoingOperation(outgoingOperationConfig, startTs, true);

            return this.$q.resolve<ChannelsModel.PromiseResult>({
              result: this.channelsConstants.promiseStatus.PresenceUpdateOk
            });
          })
          .catch((error: any) => {
            let errorInfo: any = {
              message: "Agent availability update failed",
              oldState: oldState,
              newState: params.modalityDetails[0]
            };
            this.loggingService.logError(errorInfo);
            this.sdkChannelsService.logOutgoingOperation(outgoingOperationConfig, startTs, false, error);
            return this.$q.reject({
              result: this.channelsConstants.promiseStatus.PresenceUpdateFailed,
              error: error
            });
          });
      })
      .catch((error: any) => {
        throw error;
      });
  }

  /**
   * Update the presence state for another agent
   * @param agentId: the target agent aad oid
   * @param newState: the availability state object
   * @param curState: the target agent current State
   */
  public updateAgentPresence(
    agentId: string,
    newState: AvailabilityModels.ModalityState,
    curState: AvailabilityModels.ModalityState
  ): ng.IPromise<ChannelsModel.PromiseResult> {
    return this.initialize()
      .then(() => {
        let params = {
          targetAgentId: agentId,
          modalityDetails: [
            {
              modality: newState.modality.getValue(),
              availabilityStatus: newState.cantileverState.availabilityStatus.getValue(),
              reason: newState.cantileverState.reason
            }
          ]
        };
        let outgoingOperationConfig: OutgoingQosEvent = {
          dependencyOperationName: "setAvailability",
          dependencyName: "agentSdk",
          isSuccess: false,
          latencyMs: 0,
          operationName: "updateAgentPresence",
          requestMethod: "post",
          responseStatusCode: "0",
          requestProtocol: "0",
          targetUri: "agentSdk.setAvailability"
        };
        let startTs = window.performance.now();
        return this.agentSdk
          .setAvailability(params)
          .then(() => {
            this.sdkChannelsService.logOutgoingOperation(outgoingOperationConfig, startTs, true);
            return this.$q.resolve<ChannelsModel.PromiseResult>({
              result: this.channelsConstants.promiseStatus.PresenceUpdateOk
            });
          })
          .catch((error: any) => {
            this.sdkChannelsService.logOutgoingOperation(outgoingOperationConfig, startTs, false, error);
            return this.$q.reject({
              result: this.channelsConstants.promiseStatus.PresenceUpdateFailed,
              error: error
            });
          });
      })
      .catch((error: any) => {
        throw error;
      });
  }

  public getAvailabilityStatus(): ChannelsModel.CantileverAvailabiltyStates {
    return this.agentSdk ? this.agentSdk.availabilityStatus : undefined;
  }

  public $onDestroy() {
    this.callbacks.clear();
    this.unSubsribeToEvents();
  }

  // -- PRIVATE Methods --

  private initialize(): ng.IPromise<ChannelsModel.PromiseResult> {
    if (this.isInitialized) {
      return this.refreshPresence();
    }
    return this.$q.when(
      this.initializationPromise ||
        (this.initializationPromise = this.initializeAgentSdk()
          .then((promiseResult: ChannelsModel.PromiseResult) => {
            this.agentSdk = promiseResult.data as ChannelsModel.ChannelsSdk;
            return this.refreshPresence();
          })
          .then(() => {
            return this.initializeAgent();
          })
          .catch((this.initializationPromise = undefined)))
    );
  }

  private initializeAgentSdk(): ng.IPromise<ChannelsModel.PromiseResult> {
    return this.sdkChannelsService.GetSdk(Array.from(this.supportedModalitiesSet));
  }

  private initializeAgent(): ng.IPromise<ChannelsModel.PromiseResult> {
    if (this.isInitialized) {
      return this.$q.resolve({ result: this.channelsConstants.promiseStatus.AgentInitializeOk });
    }

    let presenceState: ChannelsModel.CantileverAvailabilty = {
      modalityDetails: Array.from(this.presenceMap.values())
    };
    let outgoingOperationConfig: OutgoingQosEvent = {
      dependencyOperationName: "initAgent",
      dependencyName: "agentSdk",
      isSuccess: false,
      latencyMs: 0,
      operationName: "initializeAgent",
      requestMethod: "post",
      responseStatusCode: "0",
      requestProtocol: "0",
      targetUri: "agentSdk.initAgent"
    };
    let startTs = window.performance.now();
    return this.agentSdk
      .initAgent(presenceState)
      .then(() => {
        this.subscribeToEvents();
        this.isInitialized = true;
        this.loggingService.logInfo({
          message: "AgentSDK service initialized successfully",
          state: presenceState,
          windowId: this.$window ? this.$window.name : undefined
        });
        this.sdkChannelsService.logOutgoingOperation(outgoingOperationConfig, startTs, true);
        return this.$q.resolve({ result: this.channelsConstants.promiseStatus.AgentInitializeOk });
      })
      .catch((error) => {
        this.sdkChannelsService.logOutgoingOperation(outgoingOperationConfig, startTs, false, error);
        return this.$q.reject({
          result: this.channelsConstants.promiseStatus.AgentInitializeFailed,
          error: error
        });
      });
  }

  private initializeDefaultPresence() {
    this.presenceMap.set(this.modalityConstants.WEBFORM.getValue(), {
      modality: this.modalityConstants.WEBFORM.getValue(),
      availabilityStatus: AvailabilityConstants.CantileverPresenceState.NOTAVAILABLE.getValue(),
      reason: this.channelsConstants.availabilityReason.Away
    });
  }

  private fireEvent(
    event: AvailabilityConstants.AvailabilityEvent,
    oldState?: ChannelsModel.ModalityAvailability,
    newState?: ChannelsModel.ModalityAvailability,
    message?: string,
    error?: string
  ) {
    this.callbacks.forEach((f) => {
      f(event, oldState, newState, message, error);
    });
  }

  /**
   * Refresh the presence map with current presence state for all supported modalities
   */
  private refreshPresence(): ng.IPromise<ChannelsModel.PromiseResult> {
    let deferred = this.$q.defer<ChannelsModel.PromiseResult>();
    let outgoingOperationConfig: OutgoingQosEvent = {
      dependencyOperationName: "getAvailability",
      dependencyName: "agentSdk",
      isSuccess: false,
      latencyMs: 0,
      operationName: "refreshPresence",
      requestMethod: "get",
      responseStatusCode: "0",
      requestProtocol: "0",
      targetUri: "agentSdk.getAvailability"
    };
    let startTs = window.performance.now();
    this.agentSdk
      .getAvailability()
      .then((status: ChannelsModel.ModalityAvailability[]) => {
        this.loggingService.logInfo({
          message: "refreshPresence() returned availability status data",
          status: status,
          windowId: this.$window ? this.$window.name : undefined
        });

        this.parseEventData(status);
        this.sdkChannelsService.logOutgoingOperation(outgoingOperationConfig, startTs, true, status);
        return deferred.resolve({ result: this.channelsConstants.promiseStatus.GetAvailabilityOk });
      })
      .catch((error) => {
        this.sdkChannelsService.logOutgoingOperation(outgoingOperationConfig, startTs, false, error);
        return deferred.reject({
          result: this.channelsConstants.promiseStatus.GetAvailabilityFailed,
          error: error
        });
      });

    return deferred.promise;
  }

  private parseEventData(status: ChannelsModel.ModalityAvailability[]): AvailabilityModels.ModalityAvailabilityTuple[] {
    let eventData: AvailabilityModels.ModalityAvailabilityTuple[] = [];

    if (status === undefined || status.length == 0) {
      this.fireEvent(AvailabilityConstants.AvailabilityEvent.INFO_GET_AVAILABILITY_EMPTY);
      this.sdkChannelsService.logInfo("INFO_GET_AVAILABILITY_EMPTY event sent.");
    } else {
      status.forEach((newState) => {
        let modality = newState.modality.toString();
        if (this.supportedModalitiesSet.has(modality)) {
          let oldState: ChannelsModel.ModalityAvailability = this.presenceMap.get(modality);
          if (!this.isStateEqual(oldState, newState)) {
            switch (modality) {
              case this.modalityConstants.WEBFORM.getValue():
                this.loggingService.logInfo({
                  message: "Received INFO_WEBFORM_PRESENCE_CHANGED event from availability service",
                  oldState: oldState,
                  newState: newState,
                  windowId: this.$window ? this.$window.name : undefined
                });
                this.fireEvent(
                  AvailabilityConstants.AvailabilityEvent.INFO_WEBFORM_PRESENCE_CHANGED,
                  oldState,
                  newState
                );
                this.sdkChannelsService.logInfo("Event sent", {
                  Event: AvailabilityConstants.AvailabilityEvent.INFO_WEBFORM_PRESENCE_CHANGED
                });
                break;
              case this.modalityConstants.CHAT.getValue():
                this.loggingService.logInfo({
                  message: "Received INFO_CHAT_PRESENCE_CHANGED event from availability service",
                  oldState: oldState,
                  newState: newState,
                  windowId: this.$window ? this.$window.name : undefined
                });
                this.fireEvent(AvailabilityConstants.AvailabilityEvent.INFO_CHAT_PRESENCE_CHANGED, oldState, newState);
                this.sdkChannelsService.logInfo("Event sent", {
                  Event: AvailabilityConstants.AvailabilityEvent.INFO_CHAT_PRESENCE_CHANGED
                });
                break;
            }

            this.presenceMap.set(modality, newState);
            eventData.push({ oldState, newState });
          }
        }
      });
    }

    return eventData;
  }

  private isStateEqual(
    oldState: ChannelsModel.ModalityAvailability,
    newState: ChannelsModel.ModalityAvailability
  ): boolean {
    if (!oldState || !newState) {
      return oldState == newState;
    }

    return (
      oldState.availabilityStatus == newState.availabilityStatus &&
      oldState.modality == newState.modality &&
      oldState.reason === newState.reason
    );
  }

  private updatePresenceMap(state: AvailabilityModels.ModalityState): AvailabilityModels.ModalityAvailabilityTuple {
    let oldState: ChannelsModel.ModalityAvailability = this.presenceMap.get(state.modality.getValue());
    let newState = <ChannelsModel.ModalityAvailability>{
      modality: state.modality.getValue(),
      availabilityStatus: state.cantileverState.availabilityStatus.getValue(),
      reason: state.cantileverState.reason
    };

    this.presenceMap.set(state.modality.getValue(), newState);
    return {
      oldState: oldState,
      newState: newState
    };
  }

  private subscribeToEvents() {
    this.agentSdk.event.on(this.agentSdk.events.availabilityUpdated, this.onAvailabilityUpdated);
    this.agentSdk.event.on(this.agentSdk.events.unreachableByService, this.onUnreachableByService);
    this.agentSdk.event.on(this.agentSdk.events.getAvailabilityFailed, this.onGetAvailabilityFailed);
    this.agentSdk.event.on(this.agentSdk.events.getAvailabilityRestored, this.onGetAvailabilityRestored);
    this.sdkChannelsService.logInfo("Subscribed to AgentSdk events.");
  }

  public unSubsribeToEvents() {
    this.agentSdk.event.off(this.agentSdk.events.availabilityUpdated, this.onAvailabilityUpdated);
    this.agentSdk.event.off(this.agentSdk.events.unreachableByService, this.onUnreachableByService);
    this.agentSdk.event.off(this.agentSdk.events.getAvailabilityFailed, this.onGetAvailabilityFailed);
    this.agentSdk.event.off(this.agentSdk.events.getAvailabilityRestored, this.onGetAvailabilityRestored);
    this.sdkChannelsService.logInfo("UnSubscribed to AgentSdk events.");
  }

  private onAvailabilityUpdated = (eventData: AvailabilityModels.ModalityUpdate): void => {
    if (eventData != undefined) {
      let changes: AvailabilityModels.ModalityAvailabilityTuple[] = this.parseEventData(eventData.modalityDetails);
      if (changes && changes.length > 0) {
        this.fireEvent(AvailabilityConstants.AvailabilityEvent.INFO_AVAILABILITY_UPDATED);
      }
    }
  };

  private onUnreachableByService = (): void => {
    this.loggingService.logInfo({
      message: "Received INFO_UNREACHABLE_SERVICE event from availability service",
      windowId: this.$window ? this.$window.name : undefined
    });
    this.fireEvent(AvailabilityConstants.AvailabilityEvent.INFO_UNREACHABLE_SERVICE);
  };

  private onGetAvailabilityFailed = (errorObject: any): void => {
    this.loggingService.logInfo({
      message: "Received INFO_GET_AVAILABILITY_FAILED event from availability service",
      windowId: this.$window ? this.$window.name : undefined
    });
    this.fireEvent(AvailabilityConstants.AvailabilityEvent.INFO_GET_AVAILABILITY_FAILED);
  };

  private onGetAvailabilityRestored = (): void => {
    this.loggingService.logInfo({
      message: "Received INFO_GET_AVAILABILITY_RESTORED event from availability service",
      windowId: this.$window ? this.$window.name : undefined
    });
    this.fireEvent(AvailabilityConstants.AvailabilityEvent.INFO_GET_AVAILABILITY_RESTORED);
  };
}
